<template>
  <aside class="sidebar">
    <router-link to="/" class="brand">
      <span class="brand-io">IO</span><span class="brand-name">Academy</span>
    </router-link>

    <nav class="nav" aria-label="Main">
      <router-link to="/" class="nav-link" :class="{ active: isCoursesRoute }">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M2 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H2z" />
          <path d="M22 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8z" />
        </svg>
        <span>Courses</span>
      </router-link>
    </nav>

    <div class="user">
      <template v-if="authStore.isAuthenticated">
        <div class="user-info">
          <span class="avatar">{{ initials }}</span>
          <span class="email" :title="authStore.user?.email">{{ authStore.user?.email }}</span>
        </div>
        <button type="button" class="signout" @click="handleLogout">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
          <span>Sign out</span>
        </button>
      </template>
      <router-link v-else to="/login" class="signin">Sign in</router-link>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

const isCoursesRoute = computed(() => route.name === 'Home' || route.path.startsWith('/courses'));

const initials = computed(() => {
  const email = authStore.user?.email;
  return email ? email.slice(0, 2).toUpperCase() : '?';
});

const handleLogout = async () => {
  await authStore.logout();
  await router.push('/login');
};
</script>

<style scoped>
.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  background: var(--io-dark);
  color: #fff;
  z-index: 40;
}

.brand {
  display: block;
  padding: 1.25rem 1.25rem 1.1rem;
  font-family: var(--font-display);
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  border-bottom: 1px solid var(--io-accent-2);
}

.brand-io {
  color: #fff;
}

.brand-name {
  color: var(--io-green);
  margin-left: 0.25rem;
}

.nav {
  flex: 1;
  padding: 0.75rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.72);
  transition: background 0.15s, color 0.15s;
}

.nav-link:hover {
  color: #fff;
  background: var(--io-accent-2);
}

.nav-link.active {
  color: var(--io-green);
  background: rgba(113, 247, 159, 0.12);
}

.icon {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
}

.user {
  padding: 0.75rem;
  border-top: 1px solid var(--io-accent-2);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0.5rem;
  min-width: 0;
}

.avatar {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: var(--io-green);
  color: var(--io-dark);
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signout {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.72);
  transition: background 0.15s, color 0.15s;
}

.signout:hover {
  color: #fff;
  background: var(--io-accent-2);
}

.signin {
  display: block;
  text-align: center;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: var(--io-green);
  color: var(--io-dark);
  font-size: 0.85rem;
  font-weight: 600;
}

.signin:hover {
  background: var(--io-green-dark);
}

@media (max-width: 768px) {
  .sidebar {
    display: none;
  }
}
</style>
